import { action } from '@/lib/api';
import clone from '@/lib/clone';
import findStepApp from '@/lib/findStepApp';
import sendToast from '@/lib/sendToast';
import setQuery from '@/lib/setQuery';
import pushChangesQueueItem from '@/redux/actions/pushChangesQueueItem';
import { store } from '@/redux/lib/configureStore';
import selectors from '@/redux/selectors/selectors';
import setActiveSteps from '@/screens/workflows/redux/actions/setActiveSteps';
import { duplicateWorkflowStepsByIds } from '@shared/graph/duplicateWorkflowStepsByIds.js';
import { getParentGraphStep } from '@shared/graph/getParentGraphStep.js';
import { removeWorkflowStepsByIds } from '@shared/graph/removeWorkflowStepsByIds.js';
import { reorderGraph, validateReorderGraphRequest } from '@shared/graph/reorderGraph.js';
import { getDeep } from '@shared/utils/getDeep.js';
import { randomId } from '@shared/utils/randomId.js';
import { setDeep } from '@shared/utils/setDeep.js';
import { createWorkflow } from '@shared/workflow/createWorkflow.js';
import { createWorkflowStep } from '@shared/workflow/createWorkflowStep.js';
import workflowScreenSelectors from '../selectors/workflowScreenSelectors';

const reorderGraphMessages = {
  no_steps: 'No steps selected to move.',
  invalid_relation: "Can't move steps there.",
  invalid_branch_index: "Can't move steps into that branch.",
  missing_parent: "Can't move steps there. The drop target is no longer available.",
  target_inside_moved_structure: "Can't move a step inside itself."
};

const silentReorderGraphReasons = new Set(['target_is_moved_step']);

const workflowActions = {
  set(path, payload, workflowId) {
    return new Promise((resolve) => {
      const state = store.getState();
      const activeWorkflow = workflowId ? workflowScreenSelectors.workflowById(state, workflowId) : workflowScreenSelectors.activeWorkflow(state);

      const newWorkflow = setDeep(activeWorkflow, path, payload);
      const value = getDeep(path, newWorkflow);

      action('PUT_WORKFLOW', newWorkflow);

      // We don't want to persist blacklisted changes to the server.
      const pathBlacklist = ['variables'];
      const isInBlacklist = pathBlacklist.some((blacklistedPath) => path?.startsWith(blacklistedPath));
      if (!isInBlacklist) {
        pushChangesQueueItem('workflow', { workflowId: workflowId || activeWorkflow?.id, path, value });
      }

      resolve();
    });
  },

  /* the difference from set is that this changes at workflow row level */
  update(path, payload, workflowId, workflowVersion) {
    return new Promise((resolve) => {
      const state = store.getState();
      const workflow = workflowId ? workflowScreenSelectors.workflowById(state, workflowId, workflowVersion) : workflowScreenSelectors.activeWorkflow(state);
      const activeWorkflow = clone(workflow);

      const newWorkflow = setDeep(activeWorkflow, path, payload);
      const value = getDeep(path, newWorkflow);

      action('PUT_WORKFLOW', { ...newWorkflow, id: activeWorkflow?.id, version: workflowVersion || newWorkflow?.version });
      pushChangesQueueItem('workflow', { operation: 'update', workflowId: activeWorkflow?.id, workflowVersion: workflowVersion || newWorkflow?.version, path, value });

      resolve();
    });
  },

  /* Use this to update values from the meta column in the workflow table or values that don't depend on workflow version. */
  setMetadata(path, payload, workflowId) {
    return new Promise(async (resolve) => {
      const state = store.getState();
      const workflowMetadata = workflowId ? selectors.workflowMetadataById(state, workflowId) : workflowScreenSelectors.activeWorkflowMetadata(state);

      const newWorkflowMetadata = setDeep(workflowMetadata, path, payload);
      const value = getDeep(path, newWorkflowMetadata);

      action('PUT_WORKFLOW_METADATA', newWorkflowMetadata);
      pushChangesQueueItem('workflow', { workflowId: workflowId || workflowMetadata?.id, path, value, isMetadata: true });

      resolve();
    });
  },

  addStep({ slug, parentStepId, type, relation, index, actionId, args, options, credentialId, insertBefore = false }) {
    return new Promise(async (resolve) => {
      const state = store.getState();
      const app = selectors.appsMetadataBySlug(state, slug);
      const appDevMode = selectors.appDevModeEnabled(state);
      const companyId = selectors.companyId(state);
      const workflow = clone(workflowScreenSelectors.activeWorkflow(state));
      const stepId = randomId(12, 'all');
      const userId = selectors.userId(state);

      setQuery({ stepTab: undefined });

      let actualParentId = parentStepId;
      let actualRelation = relation;
      let actualIndex = index;

      if (insertBefore && parentStepId) {
        const originalParent = getParentGraphStep(parentStepId, workflow.graph);
        if (originalParent) {
          actualParentId = originalParent.id;
          actualRelation = originalParent.relation;
          actualIndex = originalParent.index;
        }
      }

      const isFunctionInputs = app?.slug === 'functionInputs';
      const appVersion = isFunctionInputs || !appDevMode ? app?.publishedVersion : 'draft';

      const payload = {
        id: stepId,
        companyId,
        workflowId: workflow?.id,
        appId: app?.id,
        appVersion,
        parentStepId: actualParentId,
        relation: actualRelation,
        index: actualIndex,
        type,
        userId,
        actionId,
        arguments: args,
        options,
        credentialId
      };

      try {
        const { step, graph } = await createWorkflowStep({
          data: payload,
          workflow,
          app,
          insertBefore: insertBefore ? parentStepId : null
        });

        const steps = [...(workflow?.steps || []), step];
        const newWorkflow = { ...workflow, graph, steps };

        action('PUT_WORKFLOW', newWorkflow);
        pushChangesQueueItem('workflow', { workflowId: workflow?.id, path: 'graph', value: graph ?? {} });
        pushChangesQueueItem('workflowStep', { operation: 'add', workflowId: workflow?.id, stepId: step?.id, value: step });
        resolve(step);
      } catch (error) {
        const message = error?.message || 'Failed to add step.';
        sendToast({ message, type: 'warning' });
        resolve(null);
      }
    });
  },

  deleteSteps(ids = [], keepChildren = false) {
    return new Promise((resolve) => {
      const state = store.getState();
      const activeWorkflow = clone(workflowScreenSelectors.activeWorkflow(state));
      const { deletedStepIds, deletedScheduleStepIds, steps, graph } = removeWorkflowStepsByIds(activeWorkflow, ids, keepChildren);

      // cleanup triggers if they exist
      const stepTriggers = activeWorkflow?.steps?.filter((step) => step?.type === 'trigger' && ids.includes(step?.id));
      for (const stepTrigger of stepTriggers) {
        const stepApp = findStepApp(stepTrigger, state?.workflow?.appsVersions);
        const triggerData = state?.workflow?.workflowStepTriggerData?.[stepTrigger?.id];
        const hasTriggerDataToCleanup = !!triggerData?.webhookId;
        const deleteTriggerAction = stepApp?.actions?.find((action) => action?.slug === 'deleteTrigger');

        if (!deleteTriggerAction) {
          console.error('no delete trigger action found, it should be implemented in the app screen');
          continue;
        }

        if (deleteTriggerAction && hasTriggerDataToCleanup) {
          action('CLEANUP_TRIGGERS_QUEUE', { actionId: deleteTriggerAction?.id, step: stepTrigger });
        }
      }

      action('PUT_WORKFLOW', { ...activeWorkflow, steps, graph });
      pushChangesQueueItem('workflow', { workflowId: activeWorkflow?.id, path: 'graph', value: graph });
      pushChangesQueueItem('workflowStep', { operation: 'delete', workflowId: activeWorkflow?.id, ids: deletedStepIds });
      if (deletedScheduleStepIds?.length) {
        pushChangesQueueItem('workflowSchedule', { operation: 'delete', workflowId: activeWorkflow?.id, ids: deletedScheduleStepIds });
      }
      resolve();
    });
  },

  duplicateSteps({ ids = [], parentStepId, copiedSteps }) {
    return new Promise((resolve) => {
      const state = store.getState();
      const activeWorkflow = clone(workflowScreenSelectors.activeWorkflow(state));
      const userId = selectors.userId(state);
      const changesQueue = state?.workflow?.changesQueue || [];
      const currentNewSteps = changesQueue?.find((change) => change.type === 'workflowStep' && change.operation === 'addMany')?.value || [];

      const result = duplicateWorkflowStepsByIds({
        workflow: activeWorkflow,
        ids,
        copiedSteps: copiedSteps?.steps || copiedSteps,
        copiedGraph: copiedSteps?.graph,
        userId,
        parentStepId
      });
      if (!result) {
        const message = "Can't duplicate steps there.";
        sendToast({ message, type: 'warning' });
        resolve({ success: false, message });
        return;
      }
      const { graph, steps, newSteps } = result;

      action('PUT_WORKFLOW', { ...activeWorkflow, steps, graph });
      setActiveSteps({ steps: newSteps.map((n) => n.id) });
      pushChangesQueueItem('workflow', { workflowId: activeWorkflow?.id, path: 'graph', value: graph });
      pushChangesQueueItem('workflowStep', { operation: 'addMany', workflowId: activeWorkflow?.id, value: [...currentNewSteps, ...newSteps] });
      resolve({ success: true, graph, steps, newSteps });
    });
  },

  reorderGraph({ stepIds, relation, index, parentStepId }) {
    return new Promise((resolve) => {
      const state = store.getState();
      const activeWorkflow = clone(workflowScreenSelectors.activeWorkflow(state));
      const validation = validateReorderGraphRequest({ graph: activeWorkflow?.graph, stepIds, parentStepId, relation, index });
      if (!validation.valid) {
        if (silentReorderGraphReasons.has(validation.reason)) {
          resolve({ success: false, reason: validation.reason });
          return;
        }

        const message = reorderGraphMessages[validation.reason] || "Can't move steps there.";
        sendToast({ message, type: 'warning' });
        resolve({ success: false, message, reason: validation.reason });
        return;
      }

      const newGraph = reorderGraph({ graph: activeWorkflow?.graph, stepIds, parentStepId, relation, index });

      const newWorkflow = setDeep(activeWorkflow, 'graph', newGraph);
      action('PUT_WORKFLOW', newWorkflow);
      pushChangesQueueItem('workflow', { workflowId: activeWorkflow?.id, path: 'graph', value: newGraph });
      resolve({ success: true, graph: newGraph, stepIds: validation.stepIds });
    });
  },

  createWorkflow({ integrationId, workflow, meta }) {
    return new Promise(async (resolve) => {
      const state = store.getState();
      const companyId = selectors.companyId(state);
      const userId = selectors.userId(state);

      const newWorkflow = createWorkflow({
        workflow: {
          ...workflow,
          version: 'draft',
          publishedVersion: null
        },
        meta: {
          arguments: [],
          ...meta
        },
        userId,
        companyId,
        integrationId
      });

      action('POST_WORKFLOW', newWorkflow);
      pushChangesQueueItem('workflow', { operation: 'add', workflowId: newWorkflow?.id, value: newWorkflow });
      resolve(newWorkflow);
    });
  },

  deleteWorkflow(id) {
    return new Promise((resolve) => {
      action('DELETE_WORKFLOW', { id });
      pushChangesQueueItem('workflow', { operation: 'delete', workflowId: id });
      resolve();
    });
  }
};

export default workflowActions;
